import { addDays, differenceInCalendarDays, format, isValid, isWeekend, parseISO } from 'date-fns';
import { getEasternDateKey, isSbgHoliday } from '../../../utils/timeOffUsage';

/** Every workday in a request counts as a full working day. */
export const HOURS_PER_DAY = 8;

const isUsableDate = (date: Date | null | undefined): date is Date => Boolean(date) && isValid(date);

/**
 * Workdays between two dates, inclusive of both ends: weekdays that aren't SBG
 * company holidays. This is the same rule as calendar-imported time off
 * (backend getBusinessDaysBetweenDates) and the usage math in
 * utils/timeOffUsage.ts, so a request over Thanksgiving isn't charged for it.
 *
 * Intentional data fix in the 2026 rebrand: the old form used
 * `ceil(differenceInHours(end, start) / 24)` days, which left out the end day,
 * so a single-day request came to 0 hours (and the list hides 0-hour rows).
 * Counting both ends gives a same-day request 8 hours and Fri → Mon 16 hours.
 */
export const countWorkdays = (start: Date | null, end: Date | null) => {
  if (!isUsableDate(start) || !isUsableDate(end)) return 0;
  const days = differenceInCalendarDays(end, start) + 1;
  let workdays = 0;
  for (let i = 0; i < days; i += 1) {
    const day = addDays(start, i);
    if (!isWeekend(day) && !isSbgHoliday(day)) workdays += 1;
  }
  return workdays;
};

/** The hours a range adds up to: 8 per workday. */
export const hoursForRange = (start: Date | null, end: Date | null) => countWorkdays(start, end) * HOURS_PER_DAY;

/** Why a day has no hours to take off ("a weekend", "a company holiday"), or null on a workday. */
export const dayOffReason = (day: Date): string | null => {
  if (isWeekend(day)) return 'a weekend';
  if (isSbgHoliday(day)) return 'a company holiday';
  return null;
};

/**
 * The day a one-click log books: today on the company's calendar (Eastern
 * time), as the API's date key and as a local Date for the weekend and
 * holiday checks. The browser's own calendar can differ: after 9 PM Pacific,
 * Eastern is already tomorrow.
 */
export const quickLogDay = (now: Date = new Date()) => {
  const key = getEasternDateKey(now);
  return { key, date: parseISO(key) };
};

export interface TimeOffRangeErrors {
  startDate?: string;
  endDate?: string;
}

/**
 * What's wrong with a requested range, field by field: a missing or half-typed
 * date, an end before the start, or days that are all weekends or holidays.
 * Both time-off forms use it, so neither can save a range the API would store
 * as 0 hours or backwards.
 */
export const validateTimeOffRange = (start: Date | null, end: Date | null): TimeOffRangeErrors => {
  const errors: TimeOffRangeErrors = {};
  if (!start) errors.startDate = 'Choose the first day off.';
  else if (!isValid(start)) errors.startDate = 'Enter the first day as MM/DD/YYYY.';
  if (!end) errors.endDate = 'Choose the last day off.';
  else if (!isValid(end)) errors.endDate = 'Enter the last day as MM/DD/YYYY.';
  if (errors.startDate || errors.endDate || !start || !end) return errors;
  if (differenceInCalendarDays(end, start) < 0) {
    errors.endDate = 'The last day can’t be before the first day.';
  } else if (countWorkdays(start, end) === 0) {
    errors.endDate = 'These days are all weekends or company holidays, so there are no hours to take off. Include at least one workday.';
  }
  return errors;
};

/**
 * What's wrong with hours entered by hand (HR's partial days): not a number,
 * not more than 0, or more than the range's workdays allow. Undefined when
 * the hours are fine. Check the range first; this assumes it's valid.
 */
export const validateTimeOffHours = (hours: number, start: Date | null, end: Date | null): string | undefined => {
  if (!Number.isFinite(hours) || hours <= 0) return 'Enter the hours off, more than 0.';
  const max = hoursForRange(start, end);
  if (max > 0 && hours > max) {
    const workdays = max / HOURS_PER_DAY;
    return `That’s more than ${max} hours, the most ${workdays === 1 ? 'this workday allows' : `these ${workdays} workdays allow`}.`;
  }
  return undefined;
};

/** The API stores calendar dates as `yyyy-MM-dd`. */
export const toApiDate = (date: Date) => format(date, 'yyyy-MM-dd');

/**
 * The reason an API call failed, as the backend wrote it
 * (`{ success: false, error: '…' }`), ending in a period. `null` when the
 * request never got an answer or the answer carried no message.
 */
export { apiErrorMessage as apiErrorReason } from '../../../utils/requestErrors';
